import { cloudApi } from './http.js'

const projectPath = (tenantId, projectId) => (
  `/api/tenants/${encodeURIComponent(tenantId)}/projects/${encodeURIComponent(projectId)}`
)

export const projectWorkspaceApi = Object.freeze({
  activateReportPersonas(tenantId, projectId, batchId, body) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/report-persona-proposals/${encodeURIComponent(batchId)}/activate`, body)
  },
  reportPersonaProposals(tenantId, projectId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/report-persona-proposals`)
  },
  reviewReportPersona(tenantId, personaId, body) {
    return cloudApi.patch(`/api/tenants/${encodeURIComponent(tenantId)}/operator-personas/${encodeURIComponent(personaId)}/review`, body)
  },
  personaRepairPreview(tenantId, projectId, batchId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-matrix-batches/${encodeURIComponent(batchId)}/repair-preview`)
  },
  personaMatrix(tenantId, projectId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-matrix`, { timeoutMs: 60_000 })
  },
  refreshPersonaMatrix(tenantId, projectId) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/persona-matrix:refresh`, null, { timeoutMs: 60_000 })
  },
  resumePersonaGeneration(tenantId, projectId, jobId) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/persona-generation-jobs/${encodeURIComponent(jobId)}:resume`, null)
  },
  personaGenerationProgress(tenantId, projectId, jobId = '') {
    const query = jobId ? `?job_id=${encodeURIComponent(jobId)}` : ''
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-matrix:progress${query}`)
  },
  currentPersonaBatch(tenantId, projectId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-matrix-batches/current`)
  },
  personaSeats(tenantId, batchId) {
    return cloudApi.get(`/api/tenants/${encodeURIComponent(tenantId)}/persona-matrix-batches/${encodeURIComponent(batchId)}/seats?limit=20`)
  },
  personaAssignments(tenantId, projectId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-assignments`)
  },
  dailyTaskBatches(tenantId, projectId, filters = {}) {
    const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== ''))
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-daily-tasks/batches?${query}`)
  },
  dailyTaskOverview(tenantId, projectId, localDate = '', dispatchBatchId = '', options = {}) {
    const query = new URLSearchParams()
    if (localDate) query.set('local_date', localDate)
    if (dispatchBatchId) query.set('dispatch_batch_id', dispatchBatchId)
    if (options.allBatches) query.set('all_batches', 'true')
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-daily-tasks/overview?${query}`)
  },
  dailyTasks(tenantId, projectId, localDate = '', options = {}) {
    const params = new URLSearchParams()
    if (localDate) params.set('local_date', localDate)
    if (options.personaId) params.set('persona_id', options.personaId)
    if (options.dispatchBatchId) params.set('dispatch_batch_id', options.dispatchBatchId)
    params.set('limit', String(options.limit || 50))
    params.set('offset', String(options.offset || 0))
    return cloudApi.get(`${projectPath(tenantId, projectId)}/persona-daily-tasks?${params.toString()}`)
  },
  retryFailedDailyTaskBatch(tenantId, projectId, dispatchBatchId, idempotencyKey) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/persona-daily-tasks/batches/${encodeURIComponent(dispatchBatchId)}:retry-failed`, {}, {
      timeoutMs: 60_000,
      idempotencyKey,
    })
  },
  stopDailyTaskBatch(tenantId, projectId, dispatchBatchId, body, idempotencyKey) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/persona-daily-tasks/batches/${encodeURIComponent(dispatchBatchId)}:stop`, body, {
      timeoutMs: 60_000,
      idempotencyKey,
    })
  },
  createPersonaInvites(tenantId, projectId, body = { expires_in_minutes: null, matrix_batch_id: null }) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/v2/persona-binding-invites/batch`, body, { timeoutMs: 60_000 })
  },
  dailyDispatchPolicy(tenantId, projectId) {
    return cloudApi.get(`${projectPath(tenantId, projectId)}/daily-dispatch-policy`)
  },
  prepareDailyDispatch(tenantId, projectId, materialIds) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/immediate-dispatches:prepare`, { material_ids: materialIds })
  },
  updateDailyDispatchPolicy(tenantId, projectId, body) {
    return cloudApi.put(`${projectPath(tenantId, projectId)}/daily-dispatch-policy`, body, { timeoutMs: 60_000 })
  },
  preflightDailyDispatch(tenantId, projectId, body) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/daily-dispatches:material-preflight`, body, { timeoutMs: 120_000 })
  },
  planDailyDispatch(tenantId, projectId, body) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/daily-dispatches:plan`, body, { timeoutMs: 60_000 })
  },
  confirmDailyDispatch(tenantId, projectId, body, idempotencyKey) {
    const endpoint = body.dispatch_mode === 'manual' ? '/immediate-dispatches:confirm' : '/daily-dispatches:confirm'
    return cloudApi.post(`${projectPath(tenantId, projectId)}${endpoint}`, body, {
      timeoutMs: 120_000,
      idempotencyKey,
    })
  },
  retryDailyTaskStage(tenantId, projectId, taskId, body, idempotencyKey) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/daily-tasks/${encodeURIComponent(taskId)}:retry-stage`, body, {
      timeoutMs: 60_000,
      idempotencyKey,
    })
  },
  reviewDailyTaskContent(tenantId, projectId, taskId, body, idempotencyKey) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/daily-tasks/${encodeURIComponent(taskId)}:content-review`, body, {
      timeoutMs: 60_000,
      idempotencyKey,
    })
  },
  decidePublicationProof(tenantId, projectId, taskId, proofId, body) {
    return cloudApi.post(`${projectPath(tenantId, projectId)}/daily-tasks/${encodeURIComponent(taskId)}/publication-proofs/${encodeURIComponent(proofId)}:decide`, body, {
      timeoutMs: 60_000,
    })
  },
})
